import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import { Button, Pill, Select, Textarea } from "../ui";
import { atLeast } from "../app/settings";
import type { Level } from "../core/schema";
import {
  createThread,
  decide,
  loadThread,
  send,
  status,
  warm,
  type LlmProfile,
  type Message,
  type Mode,
  type Proposal,
  type Thread,
} from "./client";

/**
 * The assistant (spec §9.18): a side panel, `Ctrl+/`. Three modes per thread; a cloud model shows a
 * banner. Every change the assistant would make arrives as a card — nothing changes until the person
 * applies it (the server enforces this at the scope layer; the card is only how it looks).
 * Level `standard`; `config` mode appears at `power`.
 */
export function AssistantPanel({
  workspaceId,
  level,
  open,
  onClose,
  full = false,
}: {
  workspaceId: string;
  level: Level;
  open: boolean;
  onClose: () => void;
  /** narrow screens: the whole screen instead of a panel at the side */
  full?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const uid = useId();
  const [profiles, setProfiles] = useState<LlmProfile[] | null>(null);
  const [mode, setMode] = useState<Mode>("overwhelm");
  const [llm, setLlm] = useState<"local" | "cloud">("local");
  const [thread, setThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && profiles === null) void status().then((s) => setProfiles(s?.profiles ?? []));
    if (open) input.current?.focus();
  }, [open, profiles]);

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  useEffect(() => {
    if (open && profiles?.some((p) => p.name === llm)) warm(llm);
  }, [open, profiles, llm]);

  // a local model can take minutes to load after a pause; say so instead of looking stuck
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!busy) return;
    const timer = window.setTimeout(() => setSlow(true), 15_000);
    return () => {
      window.clearTimeout(timer);
      setSlow(false);
    };
  }, [busy, messages.length]);

  const modes: Mode[] = atLeast(level, "power") ? ["overwhelm", "help", "config"] : ["overwhelm", "help"];
  const cloud = profiles?.find((p) => p.name === llm)?.cloud ?? false;

  const restart = () => {
    setThread(null);
    setMessages([]);
    setNotice(null);
  };

  const patchProposal = (p: Proposal) =>
    setMessages((ms) =>
      ms.map((m) => ({ ...m, proposals: m.proposals.map((x) => (x.id === p.id ? p : x)) })),
    );

  const submit = useCallback(async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setNotice(null);
    setDraft("");
    try {
      const th = thread ?? (await createThread(workspaceId, mode, llm));
      setThread(th);
      const replyId = `pending-${Date.now()}`;
      setMessages((ms) => [
        ...ms,
        { id: `u-${Date.now()}`, role: "user", text, proposals: [] },
        { id: replyId, role: "assistant", text: "", proposals: [] },
      ]);
      const edit = (fn: (m: Message) => Message) =>
        setMessages((ms) => ms.map((m) => (m.id === replyId ? fn(m) : m)));
      await send(th.id, text, i18n.language.slice(0, 2), (e) => {
        if (e.type === "delta") edit((m) => ({ ...m, text: m.text + e.text }));
        else if (e.type === "tool") setWorking(e.name);
        else if (e.type === "proposal") edit((m) => ({ ...m, proposals: [...m.proposals, e.proposal] }));
        else if (e.type === "notice") setNotice(e.code);
        else if (e.type === "error") setNotice(e.code);
        else if (e.type === "done") edit((m) => ({ ...m, id: e.messageId, proposals: e.proposals }));
      });
    } catch (err) {
      setNotice((err as Error).message);
    } finally {
      setBusy(false);
      setWorking(null);
      input.current?.focus();
    }
  }, [draft, busy, thread, workspaceId, mode, llm, i18n.language]);

  const act = async (p: Proposal, action: "apply" | "dismiss") => {
    if (!thread) return;
    try {
      patchProposal(await decide(thread.id, p.id, action));
    } catch (err) {
      setNotice((err as Error).message);
      const fresh = await loadThread(thread.id).catch(() => null);
      if (fresh) setMessages(fresh.messages);
    }
  };

  if (!open) return null;
  const none = profiles !== null && profiles.length === 0;

  return (
    <aside
      className={clsx(
        "kss-surface fixed z-40 flex flex-col overflow-hidden",
        full
          ? "inset-0 rounded-none pb-[env(safe-area-inset-bottom)]"
          : "bottom-4 right-4 top-16 w-[min(400px,calc(100vw-2rem))]",
      )}
      aria-label={t("assistant.title")}
      data-testid="assistant-panel"
    >
      <div className="flex items-center gap-2 border-b border-neutral-200 px-3 py-2 dark:border-neutral-800">
        <h2 className="text-[13.5px] font-semibold">{t("assistant.title")}</h2>
        <label htmlFor={`${uid}-mode`} className="sr-only">
          {t("assistant.modeLabel")}
        </label>
        <Select
          id={`${uid}-mode`}
          value={mode}
          disabled={!!thread}
          onChange={(e) => setMode(e.target.value as Mode)}
          className="h-8 text-[12.5px]"
          data-testid="assistant-mode"
        >
          {modes.map((m) => (
            <option key={m} value={m}>
              {t(`assistant.mode.${m}`)}
            </option>
          ))}
        </Select>
        {(profiles?.length ?? 0) > 1 && (
          <>
            <label htmlFor={`${uid}-llm`} className="sr-only">
              {t("assistant.modelLabel")}
            </label>
            <Select
              id={`${uid}-llm`}
              value={llm}
              disabled={!!thread}
              onChange={(e) => setLlm(e.target.value as "local" | "cloud")}
              className="h-8 text-[12.5px]"
            >
              {profiles!.map((p) => (
                <option key={p.name} value={p.name}>
                  {t(`assistant.llm.${p.name}`)} · {p.model}
                </option>
              ))}
            </Select>
          </>
        )}
        <span className="ml-auto flex gap-1">
          {thread && (
            <Button size="sm" variant="ghost" onClick={restart} data-testid="assistant-new">
              {t("assistant.new")}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose} aria-label={t("assistant.close")}>
            ×
          </Button>
        </span>
      </div>
      {cloud && (
        <p
          role="note"
          className="border-b border-neutral-200 bg-[var(--surface-2)] px-3 py-2 text-[12px] dark:border-neutral-800"
          data-testid="assistant-cloud-banner"
        >
          {t("assistant.cloudBanner", { model: profiles?.find((p) => p.name === llm)?.model ?? "" })}
        </p>
      )}
      <div
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto *:shrink-0 px-3 py-3"
        aria-live="polite"
      >
        {none && <p className="text-[13px] text-neutral-500">{t("assistant.notConfigured")}</p>}
        {!none && messages.length === 0 && (
          <p className="text-[13px] text-neutral-500">{t(`assistant.intro.${mode}`)}</p>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={m.role === "user" ? "self-end" : "self-start"}
            data-testid={`assistant-${m.role}`}
          >
            {m.text && (
              <p
                className={
                  m.role === "user"
                    ? "rounded-md bg-accent/10 px-3 py-2 text-[13.5px]"
                    : "whitespace-pre-wrap text-[13.5px] leading-relaxed"
                }
              >
                {m.text}
              </p>
            )}
            {m.proposals.map((p) => (
              <ProposalCard
                key={p.id}
                p={p}
                ready={!busy}
                onApply={() => void act(p, "apply")}
                onDismiss={() => void act(p, "dismiss")}
              />
            ))}
          </div>
        ))}
        {busy && (
          <p className="text-[12px] text-neutral-500" role="status">
            {working ? t("assistant.looking") : t("assistant.thinking")}
            {slow && ` ${t("assistant.slow")}`}
          </p>
        )}
        {notice && (
          <p className="text-[12.5px]" role="status" data-testid="assistant-notice">
            {t(`errors.${notice}`, { defaultValue: t("error.generic") })}
          </p>
        )}
        <div ref={end} />
      </div>
      <form
        className="flex items-end gap-2 border-t border-neutral-200 p-3 dark:border-neutral-800"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label htmlFor={`${uid}-input`} className="sr-only">
          {t("assistant.inputLabel")}
        </label>
        <Textarea
          id={`${uid}-input`}
          ref={input}
          rows={2}
          value={draft}
          disabled={none}
          placeholder={t(`assistant.placeholder.${mode}`)}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          className="flex-1 text-[13.5px]"
          data-testid="assistant-input"
        />
        <Button type="submit" disabled={busy || !draft.trim() || none} data-testid="assistant-send">
          {t("assistant.send")}
        </Button>
      </form>
    </aside>
  );
}

function ProposalCard({
  p,
  ready,
  onApply,
  onDismiss,
}: {
  p: Proposal;
  ready: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="mt-2 flex flex-col gap-1.5 rounded-md border border-accent/40 bg-accent/5 p-3"
      data-testid="assistant-proposal"
      data-status={p.status}
    >
      {p.kind === "step" ? (
        <>
          <p className="text-[13.5px] font-medium">
            {t(`assistant.action.${p.action}`, { title: p.taskTitle })}
          </p>
          {p.reason && <p className="text-[12.5px] text-neutral-500">{p.reason}</p>}
          {p.subtasks && p.subtasks.length > 0 && (
            <ul className="list-disc pl-5 text-[12.5px]">
              {p.subtasks.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <>
          <p className="text-[13.5px] font-medium">{p.summary}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[12px]">
            {Object.entries(p.args ?? {}).map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-neutral-500">{k}</dt>
                <dd className="break-all font-mono">{typeof v === "string" ? v : JSON.stringify(v)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
      {p.status === "pending" ? (
        <div className="mt-1 flex gap-2">
          <Button size="sm" onClick={onApply} disabled={!ready} data-testid="assistant-apply">
            {t("assistant.apply")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            disabled={!ready}
            data-testid="assistant-dismiss"
          >
            {t("assistant.dismiss")}
          </Button>
        </div>
      ) : (
        <Pill tone={p.status === "applied" ? "ok" : "neutral"}>{t(`assistant.status.${p.status}`)}</Pill>
      )}
    </div>
  );
}
